import { Image as ImageExtension } from '@tiptap/extension-image';
import { mergeAttributes } from '@tiptap/core';

/**
 * Custom Image extension that supports:
 * - width and height attributes
 * - alt text
 * - link (wraps image in <a> tag if href is provided)
 * - alignment (left, center, right)
 */
export const CustomImage = ImageExtension.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
        parseHTML: element => element.getAttribute('src'),
        renderHTML: attributes => {
          if (!attributes.src) {
            return {};
          }
          return {
            src: attributes.src,
          };
        },
      },
      alt: {
        default: null,
        parseHTML: element => element.getAttribute('alt'),
        renderHTML: attributes => {
          if (!attributes.alt) {
            return {};
          }
          return {
            alt: attributes.alt,
          };
        },
      },
      width: {
        default: null,
        parseHTML: element => {
          const width = element.getAttribute('width');
          return width ? parseInt(width, 10) : null;
        },
        renderHTML: attributes => {
          if (!attributes.width) {
            return {};
          }
          return {
            width: attributes.width,
          };
        },
      },
      height: {
        default: null,
        parseHTML: element => {
          const height = element.getAttribute('height');
          return height ? parseInt(height, 10) : null;
        },
        renderHTML: attributes => {
          if (!attributes.height) {
            return {};
          }
          return {
            height: attributes.height,
          };
        },
      },
      href: {
        default: null,
        parseHTML: element => {
          // Check if image is wrapped in a link
          const parent = element.parentElement;
          if (parent && parent.tagName === 'A') {
            return parent.getAttribute('href');
          }
          return null;
        },
        renderHTML: () => {
          // href is handled in renderHTML by wrapping in <a> tag
          return {};
        },
      },
      align: {
        default: null,
        parseHTML: element => {
          // Check for style attribute first (preferred), then data-align, then align attribute
          const style = element.getAttribute('style') || '';
          const styleAlign = style.match(/text-align:\s*(\w+)/)?.[1] || 
                           style.match(/float:\s*(\w+)/)?.[1];
          
          return styleAlign || 
                 element.getAttribute('data-align') || 
                 element.getAttribute('align') ||
                 null;
        },
        renderHTML: attributes => {
          if (!attributes.align) {
            return {};
          }
          
          // Use inline styles for alignment
          const align = attributes.align as string;
          let style = '';
          
          if (align === 'left') {
            style = 'float: left; margin-right: 1rem; margin-bottom: 1rem; display: inline-block;';
          } else if (align === 'right') {
            style = 'float: right; margin-left: 1rem; margin-bottom: 1rem; display: inline-block;';
          } else if (align === 'center') {
            style = 'display: block; margin-left: auto; margin-right: auto; margin-top: 1rem; margin-bottom: 1rem;';
          }
          
          return {
            style: style,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: node => {
          const img = node as HTMLImageElement;
          const parent = img.parentElement;
          
          return {
            src: img.getAttribute('src'),
            alt: img.getAttribute('alt'),
            width: img.getAttribute('width') ? parseInt(img.getAttribute('width') || '0', 10) : null,
            height: img.getAttribute('height') ? parseInt(img.getAttribute('height') || '0', 10) : null,
            href: parent && parent.tagName === 'A' ? parent.getAttribute('href') : null,
            align: (() => {
              // Check style attribute first
              const style = img.getAttribute('style') || '';
              const styleAlign = style.match(/text-align:\s*(\w+)/)?.[1] || 
                               style.match(/float:\s*(\w+)/)?.[1];
              return styleAlign || 
                     img.getAttribute('data-align') || 
                     img.getAttribute('align') || 
                     null;
            })(),
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    // href becomes a wrapping <a>; align is rendered via its own attribute renderHTML.
    const { href, ...rest } = node.attrs;
    const imgAttributes = Object.fromEntries(Object.entries(rest).filter(([key]) => key !== 'align'));
    
    const imgAttrs = mergeAttributes(this.options.HTMLAttributes, imgAttributes, HTMLAttributes);
    
    // Alignment is now handled via inline styles in the align attribute's renderHTML
    // No need to add classes or data attributes here
    
    const img = ['img', imgAttrs] as const;

    // If href is provided, wrap image in a link
    if (href) {
      return ['a', { href, target: '_blank', rel: 'noopener noreferrer' }, img];
    }

    return img;
  },
});

